import { createSelector } from '@reduxjs/toolkit';
import { UserRoles } from '../../common/enums/enums';
import { companiesFilter } from '../../helpers/users';

const selectUser = state => state.userData;

export const selectUserData = createSelector(
  selectUser,
  state => state.userData
);

export const selectSaveSuccessful = createSelector(
  selectUser,
  state => state.saveSuccessful
);

export const selectShowConfirmation = createSelector(
  selectUser,
  state => state.showConfirmation
);

export const selectIsLoading = createSelector(
  selectUser,
  state => state.loading
);

export const selectCurrentUserRole = createSelector(
  selectUser,
  state => state.currentUserRole
);

export const selectUserPerson = createSelector(
  selectUser,
  state => state?.person
);

export const selectUserPersonStatus = createSelector(
  selectUser,
  state => state?.person?.status
);

export const selectPersonId = createSelector(
  selectUserPerson,
  state => state?.personId
);

export const selectCompanyId = createSelector(
  selectUserPerson,
  state => state.company.companyId
);

export const selectDepartments = createSelector(
  selectUserPerson,
  state => state.departments || []
);

export const selectUserNotificationsSettings = createSelector(
  selectUserPerson,
  state => state?.preference?.notifications || {}
);

export const currentUserCompanyId = createSelector(
  selectUser,
  state => state.selectedUserCompanyId
);

export const selectUserRoles = createSelector(
  selectUser,
  user => user.userRoles
);

export const isApprover = createSelector(
  selectUserPerson,
  currentUserCompanyId,
  (state, companyId) =>
    !!state?.approverRights?.length &&
    state?.approverRights.some(right => right.companyId === companyId)
);

export const isManager = createSelector(selectUserRoles, state =>
  state.includes(UserRoles.DEALERSHIP_MANAGER)
);

export const selectUserCompanies = createSelector(selectUser, state =>
  state.userCompanies.map(company => ({
    ...company,
    parentCompanyId: state.userCompanies.find(
      subCompany => subCompany.companyId === company.parentCompanyId
    )?.companyId,
  }))
);

export const selectUserCompaniesWithContracts = createSelector(
  selectUser,
  state =>
    (state.userCompanies?.length > 1 && companiesFilter(state.userCompanies)) ||
    state.userCompanies
);

export const selectCurrentUserCompany = createSelector(
  currentUserCompanyId,
  selectUserCompaniesWithContracts,
  (currentCompanyId, companies) =>
    companies?.find(company => company.companyId === currentCompanyId)
);

export const selectUserCurrentCompanyStatus = createSelector(
  selectCurrentUserCompany,
  state => state?.companyStatus === 'ACTIVE'
);

export const selectUserDepartments = createSelector(
  selectUser,
  state => state.selectedDepartments
);

export const selectUserDepartmentsApproverRights = createSelector(
  selectUserPerson,
  state => state.approverRights
);

export const selectProfileManagerDialogOpen = createSelector(
  selectUser,
  state => state.profileManagerDialogOpen
);

export const selectIsSavingProfile = createSelector(
  selectUser,
  state => state.isSavingProfile
);

export const selectSaveProfileStatus = createSelector(
  selectUser,
  state => state.saveProfileStatus
);

export const selectIsInLogin = createSelector(
  selectUser,
  state => state.isLogIn
);

export const selectVerificationCodeResent = createSelector(
  selectUser,
  state => state.verificationCodeResent
);

export const selectVerificationStatus = createSelector(
  selectUser,
  state => state.verificationStatus
);

export const selectVerificationLoading = createSelector(
  selectUser,
  state => state.verificationLoading
);

export const selectVerificationModalOpen = createSelector(
  selectUser,
  state => state.verificationModalOpen
);
